"use client";
import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRouter } from "next/navigation";
import { Search, ListFilter, X } from "lucide-react";
import "swiper/css";
import Link from "next/link";
import GlobalApi from "@/utils/GlobalApi";
import CategoryIcon from "../ui/Emoji";
import CategoryIconDesktop from "../ui/EmojiD";
import { motion } from "framer-motion";
import { FiArrowLeft } from "react-icons/fi";

function CategoryFilter({ categoryList, selectedCategory }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const router = useRouter();
  const filterRef = useRef(null);

  const toggleExpand = () => {
    if (!isExpanded) {
      setSearchTerm(""); // Зөвхөн нээгдэж байхад л оролтыг хоослоно
    }
    setIsExpanded(!isExpanded); // Нээх, хаах төлөвийг солих
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsExpanded(false);
  };

  useEffect(() => {
    if (searchTerm.trim()) {
      GlobalApi.getContentsBySearch(searchTerm).then(setSearchResults);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const handleCategoryClick = (categoryName) => {
    const encodedCategoryName = encodeURIComponent(categoryName);
    router.push(`/contents-category/${encodedCategoryName}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <div>
      {/* Desktop Garchig */}
      <motion.div className="hidden md:block md:py-12 lg:pt-14 xl:py-16">
        <div className="flex md:gap-6 lg:gap-0">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 1,
              delay: 0.2,
              type: "spring",
              stiffness: 100,
            }}
            viewport={{ once: true }}
            className="flex items-center"
          >
            <Link href={"/"}>
              <div className="md:w-[30px] md:h-[30px] lg:w-[35px] lg:h-[35px] xl:w-[40px] xl:h-[40px] 2xl:w-[45px] 2xl:h-[45px] flex items-center justify-center rounded-full bg-[#40A4D8]">
                <FiArrowLeft className="md:w-[25px] md:h-[25px] lg:w-[30px] lg:h-[30px] xl:w-[35px] xl:h-[35px] text-white" />
              </div>
            </Link>
          </motion.div>
          <div className="flex flex-col items-center md:w-[650px] lg:w-full !text-[#000000]">
            <motion.h1
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 1,
                delay: 0.2,
                type: "spring",
                stiffness: 100,
              }}
              viewport={{ once: true }}
              className="text-[24px] md:text-[28px] lg:text-[32px] xl:text-[36px] font-semibold !text-[#000000]"
            >
              Бүлэг сэдвүүд
            </motion.h1>
            <motion.span
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 1,
                delay: 0.6,
                type: "spring",
                stiffness: 100,
              }}
              viewport={{ once: true }}
              className="md:text-sm lg:text-lg xl:text-xl text-[#000000] text-center md:mt-2 lg:mt-3 xl:mt-4 md:px-10 lg:px-10 xl:px-28 2xl:px-0"
            >
              Тухайн бүлэг сэдэв болгон өөрийн гэсэн мэдээллүүдээс бүрдэх ба
              цагаан толгойн үсгээр ангилан хайж болно
            </motion.span>
          </div>
        </div>
      </motion.div>
      {/* Mobile Garchig */}
      <motion.div className="block md:hidden py-4">
        <div className="flex">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 1,
              delay: 0.2,
              type: "spring",
              stiffness: 100,
            }}
            viewport={{ once: true }}
            className="flex items-center"
          >
            <Link href={"/"}>
              <div className="w-[26px] h-[26px] flex items-center justify-center rounded-full bg-[#40A4D8]">
                <FiArrowLeft className="w-[20px] h-[20px] text-white" />
              </div>
            </Link>
          </motion.div>
        </div>
      </motion.div>
      <section className="hidden md:block">
        <div className="w-full flex flex-wrap justify-center gap-3 pb-16">
          {categoryList.map((category, index) => (
            <motion.button
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: index * 0.05,
                type: "spring",
                stiffness: 150,
              }}
              viewport={{ once: true }}
              key={category.attributes.name}
              onClick={() => handleCategoryClick(category.attributes.name)}
              className={`text-black md:text-base lg:text-lg border font-bold flex items-center border-[#40A4D8] py-2 px-3 rounded-3xl hover:bg-[#40A4D8] hover:text-white transition-colors duration-300 ease-in-out 
              ${selectedCategory === category.attributes.name && "bg-[#40A4D8] !text-white !font-bold"}`}
            >
              <div className="flex items-center gap-1 flex-nowrap">
                <div className="text-[24px]">
                  <CategoryIconDesktop emojiName={category.attributes.emoji} />
                </div>
                <h2>{category.attributes.name}</h2>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
      <section className="md:hidden overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 1,
            delay: 0.6,
            type: "spring",
            stiffness: 100,
          }}
          viewport={{ once: true }}
          className="w-full pb-5"
          ref={filterRef}
        >
          <div className="flex items-center relative w-full mb-4">
            <input
              type="text"
              className="w-full rounded-3xl h-[40px] pl-10 pr-10 bg-[#F5F5F5] focus:ring-0 focus:outline-none"
              placeholder="Хайх"
              value={searchTerm}
              onChange={handleInputChange}
            />
            <Search
              className="absolute left-3 text-[#333333]"
              size={20}
              strokeWidth={1.5}
            />
            {isExpanded ? (
              <X
                className="absolute right-3 text-[#333333] cursor-pointer"
                size={20}
                onClick={toggleExpand}
              />
            ) : (
              <ListFilter
                className="absolute right-3 text-[#333333] cursor-pointer"
                size={20}
                onClick={toggleExpand}
              />
            )}
          </div>

          {isExpanded ? (
            <div className="absolute w-full gap-2 z-50 bg-[#FCFCFC] flex flex-wrap items-center justify-center pb-5 px-5 left-0 border-b rounded-b-[24px]">
              {categoryList.map((category) => (
                <div
                  key={category.attributes.name}
                  onClick={() => handleCategoryClick(category.attributes.name)}
                  className={`text-center text-black border h-[40px] font-bold flex items-center w-fit border-[#40A4D8] p-2 rounded-3xl hover:bg-[#40A4D8] hover:text-white transition-colors duration-300 ease-in-out cursor-pointer 
                  ${selectedCategory === category.attributes.name && "bg-[#40A4D8] !text-white !font-bold"}`}
                >
                  <div className="flex justify-center items-center gap-1 text-[13px]">
                    <CategoryIcon emojiName={category.attributes.emoji} />
                    <h2>{category.attributes.name}</h2>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Swiper
              slidesPerView="auto"
              spaceBetween={12}
              pagination={{ clickable: true }}
              className="mySwiper h-[40px]"
            >
              {categoryList.map((category) => (
                <SwiperSlide
                  key={category.attributes.name}
                  onClick={() => handleCategoryClick(category.attributes.name)}
                  className={`text-black p-[9px] font-bold flex items-center text-nowrap border border-[#40A4D8] rounded-3xl hover:bg-[#40A4D8] hover:text-white transition-colors duration-300 ease-in-out 
                  ${selectedCategory === category.attributes.name && "bg-[#40A4D8] !text-white !font-bold"}`}
                  style={{ width: "auto" }}
                >
                  <div className="flex items-center justify-center gap-1 text-[13px]">
                    <CategoryIcon
                      strokeWidth={1}
                      emojiName={category.attributes.emoji}
                    />
                    <h2>{category.attributes.name}</h2>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          <div
            ref={filterRef}
            className="grid grid-cols-1 absolute justify-items-center bg-[#FCFCFC] w-full line-clamp-1 mt-5 z-50 top-32 left-0 pb-3 px-5"
          >
            {searchResults.length > 0
              ? searchResults.map((result) => (
                  <Link
                    href={"/contents/" + result.attributes.slug}
                    key={result.id}
                    className="mb-2 p-2 rounded-3xl w-full flex justify-center items-center border-b rounded-b-[24px] px-6 bg-[#F5F5F5] my-3 shadow-custom4 transition-colors duration-300 ease-in-out hover:text-[#40A4D8] hover:bg-slate-100"
                  >
                    {/* Content-rvv link hiih */}
                    <h3 className="text-center text-sm flex items-center">
                      {result.attributes.Title}
                    </h3>
                  </Link>
                ))
              : searchTerm && (
                  <span className="whitespace-nowrap full text-center pt-2">
                    Илэрц олдсонгүй "{searchTerm}".
                  </span>
                )}
          </div>
        </motion.div>
      </section>
    </div>
  );
}

export default CategoryFilter;
